/**
 * Module dependencies.
 */

import { Benefit, CardServiceBenefits } from 'src/components/core/cards/card-service-benefits';
import { CardServiceSteps, Step } from 'src/components/core/cards/card-service-steps';
import { ClientSupport } from 'src/components/sections/services/client-support';
import { ContactCard } from 'src/components/sections/services/contact-card';
import { Content, filter } from '@prismicio/client';
import { FadeInUp } from 'src/components/core/animations/fade-in-up';
import { GetStaticProps, NextPage } from 'next';
import { Heading } from 'src/components/core/heading';
import { Hero } from 'src/components/sections/hero';
import { ProductJsonLd } from 'next-seo';
import { RawHtml } from '@untile/react-core/components/raw-html';
import { SEO } from 'src/components/core/seo';
import { SSGProps } from 'src/types/app';
import { Schedule } from 'src/components/sections/services/schedule';
import { Service } from 'src/components/core/pricing-pack';
import { ServiceCarousel } from 'src/components/sections/services/carousel';
import { ServicePricing } from 'src/components/sections/services/pricing';
import { Slide } from 'src/components/core/carousel';
import { Text } from 'src/components/core/text';
import { createPrimsicClient } from 'src/prismic/client';
import { getDataFromTranslations } from 'src/core/utils/objects';
import { media } from '@untile/react-core/styles/media';
import { normalizePrismicSEO } from 'src/prismic/utils';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useScroll, useSpring, useTransform } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { withSettings } from 'src/hocs/with-settings';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  data: Content.ServiceGarantiasDocumentData;
  faqs: Content.FaqDocumentData[];
};

/**
 * `StyledHeading` styled component.
 */

const StyledHeading = styled(Heading)`
  position: relative;
  z-index: 1;

  ${media.min.md`
    max-width: 55%;
  `}
`;

/**
 * `Disclaimer` styled component.
 */

const Disclaimer = styled.div`
  color: var(--color-slate400);
  padding-top: var(--section-space);

  a {
    color: var(--color-primary);
    text-decoration: underline;

    :focus,
    :focus-visible,
    :hover {
      color: var(--color-slate200);
    }
  }

  ${media.max.md`
    padding-inline: var(--space-sm);
  `}
`;

/**
 * `ServiceGarantias` page.
 */

const ServiceGarantias: NextPage<Props> = ({ data, faqs }) => {
  const { t } = useTranslation('service-garantias');

  const { scrollYProgress } = useScroll();
  const isMobile = useBreakpoint(0, 'ms');
  const scrollSpring = useSpring(scrollYProgress, { damping: 50, stiffness: 400 });
  const heroImgParallaxY = useTransform(scrollSpring, [0, 1], ['0%', '350%']);

  const { data: benefitsList } = getDataFromTranslations<Benefit>('sections.benefits.list', t);
  const { data: scheduleSteps } = getDataFromTranslations<Step>('sections.schedule.steps', t);
  const { data: servicesPricing } = getDataFromTranslations<Service>('prices:sections.extras.services', t);

  return (
    <>
      <SEO {...normalizePrismicSEO(data)} />

      <ProductJsonLd description={t('sections.header.description')} productName={t('title')} />

      <Hero
        image={{
          alt: t('sections.hero.image.alt'),
          motionStyle: { x: '0%', y: heroImgParallaxY },
          src: isMobile ? t('sections.hero.image.mobileSrc') : t('sections.hero.image.src'),
          style: {
            backgroundPosition: '98% 5%',
            backgroundSize: 'cover'
          }
        }}
      >
        <StyledHeading
          asDescription={'p'}
          asTitle={'h1'}
          description={t('sections.hero.description')}
          hasMaxWidth
          label={t('title')}
          lead={t('sections.hero.lead')}
          title={t('sections.hero.title')}
          titleTheme={'slate50'}
        />
      </Hero>

      <ServiceCarousel
        slides={benefitsList.map(({ description, title }, index) => (
          <Slide key={index}>
            <FadeInUp style={{ height: '100%' }}>
              <CardServiceBenefits description={description} isDescriptionRaw title={title} />
            </FadeInUp>
          </Slide>
        ))}
        title={t('sections.benefits.title')}
      />

      <ServicePricing services={[{ ...servicesPricing[0], hideDetailsUrl: true, hidePackHeader: false }]} />

      <ServiceCarousel
        hasGradient
        slides={scheduleSteps.map(({ ...rest }, index) => (
          <Slide key={index}>
            <FadeInUp style={{ height: '100%' }}>
              <CardServiceSteps {...rest} />
            </FadeInUp>
          </Slide>
        ))}
        title={t('sections.schedule.title')}
      />

      <ContactCard />

      <Schedule />

      <ClientSupport
        disclaimer={
          <Disclaimer>
            <Text variant={'h4'} weight={600}>
              {t('disclaimer.title')}
            </Text>

            <Text as={'p'} style={{ margin: 'var(--space-xs) 0' }} variant={'bodyS'}>
              <RawHtml>
                {t('disclaimer.description', {
                  link1: process.env.NEXT_PUBLIC_TERMS_AND_CONDITIONS_WARRANTY_URL,
                  link2: process.env.NEXT_PUBLIC_ESSENTIAL_WARRANTY_URL,
                  link3: process.env.NEXT_PUBLIC_MAXIMUM_WARRANTY_URL
                })}
              </RawHtml>
            </Text>
          </Disclaimer>
        }
        faqs={faqs}
      />
    </>
  );
};

/**
 * Export `getStaticProps`.
 */

export const getStaticProps: GetStaticProps<SSGProps> = withSettings<any>(async ({ locale, previewData }) => {
  const client = createPrimsicClient({ previewData });
  const { data } = await client.getSingle('service_garantias');
  const faqs = await client.getAllByType('faq', {
    filters: [
      filter.at('my.faq.visible_in_garantias', true),
      filter.at('my.faq.active', true),
      filter.at('document.type', 'faq')
    ],
    orderings: {
      direction: 'asc',
      field: 'document.first_publication_date'
    }
  });

  return {
    props: {
      ...(await serverSideTranslations(locale as string, ['common', 'service-garantias', 'prices'])),
      data,
      faqs: faqs.map(({ data }) => data),
      hideNavbarBackground: true
    },
    revalidate: 60
  };
});

/**
 * Export `ServiceGarantias` page.
 */

export default ServiceGarantias;
